import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import moment from 'moment';
import _ from 'lodash';
import { environment } from '../../environments/environment';
import { Employee } from '../models/employee';
import { AbsenceDrawerComponent } from '../components/drawers/absence-drawer/absence-drawer.component';
import { AuxiliarService } from './auxiliar.service';
import { TranslateService } from '@ngx-translate/core';
import { Absence } from '../models/absence';
import { TenantService } from './tenant.service';

@Injectable({
  providedIn: 'root'
})
export class AbsenceService {
  private absencesSubject = new BehaviorSubject({} as Absence);
  public absencesObservable = this.absencesSubject.asObservable();

  constructor(
    private authService: AuthService,
    private httpClient: HttpClient,
    private auxiliarService: AuxiliarService,
    private translate: TranslateService,
    private tenantService: TenantService,
  ) { }

  // trigger absences update
  public updateEmployeeAbsences(absence: Absence): void {
    this.absencesSubject.next(absence);
  }

  // [GET] my absences
  public getMyAbsences(date: string): Observable<{ [index: string]: any; }> {
    const tenant = this.authService.getTokenFieldValue('defaultTenant');
    const headers = new HttpHeaders({ 'X-Tenant': tenant });

    let apiEndpoint = _.reduce(
      [[':id', 'me'], [':year', _.toString(moment(date).year())]],
      (res, [key, replacement]) => _.replace(res, key, replacement),
      environment.api_endpoints.absences
    );

    return this.httpClient.get<HttpResponse<object>>
      ( environment.api_server + apiEndpoint, { observe: 'response', headers } );
  }

  // [GET] employee absences
  public getEmployeeAbsences(date: string, employee: Employee): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const defaultTenant = _.get(employee, 'defaultTenant');
    const headers = new HttpHeaders({ 'X-Tenant': defaultTenant || tenant.id});
    
    let apiEndpoint = _.reduce(
      [[':id', employee.id], [':year', _.toString(moment(date).year())]],
      (res, [key, replacement]) => _.replace(res, key, replacement),
      environment.api_endpoints.absences
    );

    return this.httpClient.get<HttpResponse<object>>
      ( environment.api_server + apiEndpoint, { observe: 'response', headers } );
  }

  // [POST] create absence
  public createAbsence(absence: Absence, employee: Employee | undefined): Observable<{ [index: string]: any; }> {
    const tenant = _.get(employee, 'defaultTenant', this.authService.getTokenFieldValue('defaultTenant'));
    const headers = new HttpHeaders({ 'X-Tenant': tenant });
    return this.httpClient.post<HttpResponse<object>>
      (
        environment.api_server + _.replace(environment.api_endpoints.absencesCreateUpdate, ':id', employee?.id || 'me'),
        absence, { observe: 'response', headers }
      );
  }

  // [PATCH] update absence
  public updateAbsence(absence: Absence, employee: Employee | undefined): Observable<{ [index: string]: any; }> {
    const tenant = _.get(employee, 'defaultTenant', this.authService.getTokenFieldValue('defaultTenant'));
    const headers = new HttpHeaders({ 'X-Tenant': tenant });
    return this.httpClient.patch<HttpResponse<object>>
      (
        environment.api_server + _.replace(environment.api_endpoints.absencesCreateUpdate, ':id', absence.id),
        absence, { observe: 'response', headers }
      );
  }

  // [DELETE] absence
  public deleteAbsence(absence: Absence, employee: Employee | undefined): Observable<{ [index: string]: any; }> {
    const tenant = _.get(employee, 'defaultTenant', this.authService.getTokenFieldValue('defaultTenant'));
    const headers = new HttpHeaders({ 'X-Tenant': tenant });
    return this.httpClient.delete<HttpResponse<object>>
      (
        environment.api_server + _.replace(environment.api_endpoints.absencesCreateUpdate, ':id', absence.id),
        { observe: 'response', headers }
      );
  }

  // open absence detail
  public async openAbsenceDetail(employee: Employee, openAbsence: Absence | undefined, position: string, absence?: Absence): Promise<boolean> {
    const res = await this.auxiliarService.openDrawer(AbsenceDrawerComponent, { 
      absence,
      employee,
      openAbsence,
      position,
      iconLeft: 'pi pi-home',
      iconRight: 'pi pi-home',
      titleLeft: this.translate.instant(absence ? 'title.absence' : 'button.new-absence'),
      titleRight: this.translate.instant(absence ? 'title.absence' : 'button.new-absence'),
      width: '600px'
    });
    return res;
  }
}
